/**
 * What the build-data endpoints and the build optimizer share: the optimizer's slot names,
 * the gear scopes, the item source kinds, the candidate score, and the response shapes of
 * `/api/build-data/*`.
 */
import type { ArtifactType, DataStatus } from './artifact-types.js';
import type { BuildSlot } from './characters.js';

export { characterSlotToBuildSlot } from './characters.js';
export type { BuildSlot } from './characters.js';

export const BUILD_SLOTS = [
  'head',
  'neck',
  'shoulder',
  'back',
  'chest',
  'wrist',
  'hands',
  'waist',
  'legs',
  'feet',
  'finger1',
  'finger2',
  'trinket1',
  'trinket2',
  'mainHand',
  'offHand',
  'ranged',
] as const satisfies readonly BuildSlot[];
export const BUILD_SLOT_LABELS: Record<BuildSlot, string> = {
  head: 'Head',
  neck: 'Neck',
  shoulder: 'Shoulder',
  back: 'Back',
  chest: 'Chest',
  wrist: 'Wrist',
  hands: 'Hands',
  waist: 'Waist',
  legs: 'Legs',
  feet: 'Feet',
  finger1: 'Finger 1',
  finger2: 'Finger 2',
  trinket1: 'Trinket 1',
  trinket2: 'Trinket 2',
  mainHand: 'Main Hand',
  offHand: 'Off Hand',
  ranged: 'Ranged',
};

/** Item `data.slot` -> the build slots it can fill. */
const ITEM_SLOT_TO_BUILD: Record<string, BuildSlot[]> = {
  Head: ['head'],
  Neck: ['neck'],
  Shoulder: ['shoulder'],
  Back: ['back'],
  Chest: ['chest'],
  Wrist: ['wrist'],
  Hands: ['hands'],
  Waist: ['waist'],
  Legs: ['legs'],
  Feet: ['feet'],
  Finger: ['finger1', 'finger2'],
  Trinket: ['trinket1', 'trinket2'],
  'Main Hand': ['mainHand'],
  'One-Hand': ['mainHand', 'offHand'],
  'Two-Hand': ['mainHand'],
  'Off Hand': ['offHand'],
  Shield: ['offHand'],
  'Held In Off-hand': ['offHand'],
  Ranged: ['ranged'],
  Thrown: ['ranged'],
  Relic: ['ranged'],
};
export function buildSlotsForItemSlot(itemSlot: string | undefined): BuildSlot[] {
  return itemSlot ? [...(ITEM_SLOT_TO_BUILD[itemSlot] ?? [])] : [];
}
/** Item `data.slot` values that map to a build slot (the SQL filter for a slot query). */
export function itemSlotsForBuildSlot(slot: BuildSlot): string[] {
  return Object.entries(ITEM_SLOT_TO_BUILD)
    .filter(([, slots]) => slots.includes(slot))
    .map(([itemSlot]) => itemSlot);
}

export const BUILD_SCOPES = ['all', 'no_raids', 'solo_dungeons'] as const;
export type BuildScope = (typeof BUILD_SCOPES)[number];
export const BUILD_SCOPE_LABELS: Record<BuildScope, string> = {
  all: 'Everything obtainable at this level',
  no_raids: 'No raids',
  solo_dungeons: 'Solo and dungeons only',
};

/** Whether a build is for fighting monsters or players; PvP weighs survival and keeps PvP rewards in scope. */
export const BUILD_CONTENTS = ['pve', 'pvp'] as const;
export type BuildContent = (typeof BUILD_CONTENTS)[number];
export const BUILD_CONTENT_LABELS: Record<BuildContent, string> = { pve: 'PvE', pvp: 'PvP' };

export const ITEM_SOURCE_KINDS = ['quest', 'dungeon', 'raid', 'world', 'vendor', 'crafted', 'pvp'] as const;
export type ItemSourceKind = (typeof ITEM_SOURCE_KINDS)[number];

/** One way to obtain an item, resolved from its links at read time. */
export interface ItemSourceView {
  kind: ItemSourceKind;
  name: string;
  /** Absent for a source with no page (a PvP rank). */
  slug?: string;
  type?: ArtifactType;
  zone?: string;
}

/**
 * Whether an item with these sources is obtainable within the scope: `no_raids` hides items
 * whose only sources are raids, `solo_dungeons` also hides PvP-only items; an item with no
 * known source is always in scope.
 */
export function sourcesInScope(sources: ItemSourceView[], scope: BuildScope, content: BuildContent = 'pve'): boolean {
  if (scope === 'all' || sources.length === 0) return true;
  // A PvP build is built from PvP rewards, so no scope hides them from one.
  const excluded: ItemSourceKind[] = scope === 'no_raids' || content === 'pvp' ? ['raid'] : ['raid', 'pvp'];
  return sources.some((s) => !excluded.includes(s.kind));
}

export interface ScorableItem {
  stats?: { name: string; value: number }[];
  armor?: number;
  damage?: { min: number; max: number; speed: number };
}
/** Weights keyed by stat name, `armor` or `dps`; unknown keys score nothing. */
export type ItemWeights = Record<string, number>;

export function scoreItem(item: ScorableItem, weights: ItemWeights): number {
  let score = 0;
  for (const s of item.stats ?? []) score += (weights[s.name] ?? 0) * s.value;
  if (item.armor) score += (weights.armor ?? 0) * item.armor;
  if (item.damage && item.damage.speed > 0) score += (weights.dps ?? 0) * ((item.damage.min + item.damage.max) / 2 / item.damage.speed);
  return score;
}

// ---- /api/build-data/* responses -------------------------------------------------------

export interface BuildRulesResponse {
  dataBuild: string;
  class: string;
  race: string;
  faction: string;
  validCombination: boolean;
  proficiencies: { skill: string; minLevel: number }[];
}

export interface BuildTalentView {
  id: string;
  name: string;
  slug: string;
  tree: string;
  tier: number;
  column: number;
  maxRank: number;
  prerequisite?: { artifactId: string; rank?: number };
  iconUrl?: string;
  /** The rank 1 and top-rank descriptions. */
  descriptions: string[];
}
export interface BuildTalentsResponse {
  dataBuild: string;
  class: string;
  talents: BuildTalentView[];
}

export interface BuildCandidate {
  id: string;
  name: string;
  slug: string;
  quality?: number;
  subclass?: string;
  slot: BuildSlot;
  itemLevel?: number;
  requiredLevel?: number;
  stats?: { name: string; value: number }[];
  armor?: number;
  damage?: { min: number; max: number; speed: number };
  classes?: string[];
  dataStatus: DataStatus;
  sourceBuild: string | null;
  sources: ItemSourceView[];
  sourceUnknown: boolean;
  score: number;
}
export interface BuildItemsResponse {
  dataBuild: string;
  slot: BuildSlot;
  scope: BuildScope;
  items: BuildCandidate[];
}

export interface BuildEnchantView {
  id: number;
  text: string;
  slots: string[];
  weaponTypes?: string[];
  effects: { kind: string; name: string; value?: number }[];
  temporary: boolean;
  recipe: { id: string; name: string; slug: string; profession?: string; requiredSkill?: number };
}
export interface BuildEnchantsResponse {
  dataBuild: string;
  slot: BuildSlot;
  enchants: BuildEnchantView[];
}

export interface BuildConsumableView {
  id: string;
  name: string;
  slug: string;
  requiredLevel?: number;
  use: { text: string; duration?: string; cooldown?: string; effects: { kind: string; name: string; value?: number }[]; enchantId?: number };
}
export interface BuildWorldBuffView {
  spellId: number;
  name: string;
  grantedBy: string;
  zone: string;
  text: string;
  duration?: string;
  effects: { kind: string; name: string; value?: number }[];
}
export interface BuildConsumablesResponse {
  dataBuild: string;
  effect: string | null;
  items: BuildConsumableView[];
  worldBuffs: BuildWorldBuffView[];
}

export interface BuildProfessionRecipeView {
  id: string;
  name: string;
  slug: string;
  requiredSkill?: number;
  taught?: string;
  resultItem?: { artifactId?: string; name: string; slug?: string };
  enchant?: { id: number; text: string; slots: string[] };
}
export interface BuildProfessionsResponse {
  dataBuild: string;
  profession: string;
  recipes: BuildProfessionRecipeView[];
  /** Profession-only gear (items whose `requiredProfession` names this profession). */
  items: { id: string; name: string; slug: string; skill: number; slot?: string }[];
}
